import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Avatar,
  Breadcrumbs,
  Link,
  IconButton,
  Paper,
  Grid,
} from '@mui/material';
import {
  QrCodeScanner,
  Home,
  Assignment,
  CameraAlt,
  FlashOn,
  FlashOff,
  Refresh,
  Search,
  CheckCircle,
  Error,
  Visibility,
  ArrowBack,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import samplesService, { Sample } from '../services/samplesService';

const QRScanner: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const [isScanning, setIsScanning] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [scannedSample, setScannedSample] = useState<Sample | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [recentScans, setRecentScans] = useState<Sample[]>([]);

  useEffect(() => {
    checkCameraAvailability();
    loadRecentScans();
  }, []);

  const checkCameraAvailability = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setHasCamera(videoDevices.length > 0);
    } catch (error) {
      console.error('Error checking camera:', error);
      setHasCamera(false);
    }
  };

  const loadRecentScans = () => {
    // Load recent scans from localStorage
    const recent = localStorage.getItem('recentScans');
    if (recent) {
      try {
        setRecentScans(JSON.parse(recent));
      } catch (error) {
        console.error('Error loading recent scans:', error);
      }
    }
  };

  const saveRecentScan = (sample: Sample) => {
    const recent = [sample, ...recentScans.filter(s => s.id !== sample.id)].slice(0, 10);
    setRecentScans(recent);
    localStorage.setItem('recentScans', JSON.stringify(recent));
  };

  const startScanning = async () => {
    if (!hasCamera) {
      setError('Aucune caméra disponible');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setIsScanning(true);
        setError(null);
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      setError('Impossible d\'accéder à la caméra');
    }
  };

  const stopScanning = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  };

  const handleQRCodeDetected = async (qrData: string) => {
    try {
      setError(null);
      const sample = await samplesService.scanQRCode(qrData);
      setScannedSample(sample);
      setDialogOpen(true);
      saveRecentScan(sample);
      setSuccess(`Échantillon ${sample.sample_id} scanné avec succès`);
      stopScanning();
    } catch (error) {
      console.error('Error scanning QR code:', error);
      setError('QR code invalide ou échantillon non trouvé');
    }
  };

  const handleManualSearch = async () => {
    if (!manualCode.trim()) return;

    try {
      setError(null);
      const sample = await samplesService.getSampleBySampleId(manualCode.trim());
      setScannedSample(sample);
      setDialogOpen(true);
      saveRecentScan(sample);
      setSuccess(`Échantillon ${sample.sample_id} trouvé`);
    } catch (error) {
      console.error('Error finding sample:', error);
      setError('Échantillon non trouvé');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RECEPTION': return 'info';
      case 'PREPARATION_MECANIQUE': return 'warning';
      case 'ANALYSE_CHIMIQUE': return 'secondary';
      case 'PRET': return 'success';
      case 'EN_ATTENTE': return 'primary';
      case 'REJETE': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'RECEPTION': return 'Réception';
      case 'PREPARATION_MECANIQUE': return 'Préparation Mécanique';
      case 'ANALYSE_CHIMIQUE': return 'Analyse Chimique';
      case 'PRET': return 'Prêt';
      case 'EN_ATTENTE': return 'En Attente';
      case 'REJETE': return 'Rejeté';
      default: return status;
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/dashboard" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Home fontSize="small" />
          Tableau de bord
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <QrCodeScanner fontSize="small" />
          Scanner QR
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Scanner QR Code
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Scannez le QR code d'un échantillon pour accéder à ses informations
          </Typography>
        </Box>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Scanner Section */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CameraAlt />
                Scanner Caméra
              </Typography>

              {/* Camera View */}
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Paper 
                  sx={{ 
                    height: 400, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {isScanning ? (
                    <>
                      <video
                        ref={videoRef}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {/* Scanning overlay */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: 200,
                          height: 200,
                          border: '2px solid',
                          borderColor: 'primary.main',
                          borderRadius: 2,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -2,
                            left: -2,
                            right: -2,
                            bottom: -2,
                            border: '2px solid',
                            borderColor: 'primary.main',
                            borderRadius: 2,
                            animation: 'pulse 2s infinite',
                          }
                        }}
                      />
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <QrCodeScanner sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        Caméra non active
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cliquez sur "Démarrer Scanner" pour commencer
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Camera Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                  {!isScanning ? (
                    <Button
                      variant="contained"
                      startIcon={<CameraAlt />}
                      onClick={startScanning}
                      disabled={!hasCamera}
                    >
                      Démarrer Scanner
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        onClick={stopScanning}
                      >
                        Arrêter
                      </Button>
                      <IconButton
                        onClick={() => setFlashEnabled(!flashEnabled)}
                        color={flashEnabled ? 'primary' : 'default'}
                      >
                        {flashEnabled ? <FlashOff /> : <FlashOn />}
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>

              {/* Manual Input */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Recherche Manuelle
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <input
                    type="text"
                    placeholder="Entrez le code échantillon (ex: AFL-2024-00001)"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleManualSearch()}
                  />
                  <Button
                    variant="contained"
                    startIcon={<Search />}
                    onClick={handleManualSearch}
                    disabled={!manualCode.trim()}
                  >
                    Rechercher
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Scans */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment />
                Scans Récents
              </Typography>

              {recentScans.length > 0 ? (
                <List dense>
                  {recentScans.map((sample, index) => (
                    <ListItem
                      key={sample.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 1,
                        mb: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'grey.50'
                        }
                      }}
                      onClick={() => navigate(`/samples/${sample.id}`)}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                          {sample.sample_id.slice(-2)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={sample.sample_id}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Chip 
                              label={getStatusLabel(sample.statut)} 
                              size="small" 
                              color={getStatusColor(sample.statut)}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <QrCodeScanner sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Aucun scan récent
                  </Typography>
                </Box>
              )}

              {recentScans.length > 0 && (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={() => {
                    setRecentScans([]);
                    localStorage.removeItem('recentScans');
                  }}
                  sx={{ mt: 2 }}
                >
                  Effacer l'Historique
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sample Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            Échantillon Scanné
          </Box>
        </DialogTitle>
        <DialogContent>
          {scannedSample && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Code Échantillon</Typography>
                  <Typography variant="h6">{scannedSample.sample_id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Statut</Typography>
                  <Chip 
                    label={getStatusLabel(scannedSample.statut)} 
                    color={getStatusColor(scannedSample.statut)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Client</Typography>
                  <Typography variant="body1">{scannedSample.client.company_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Type</Typography>
                  <Typography variant="body1">{scannedSample.type_echantillon}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Projet</Typography>
                  <Typography variant="body1">{scannedSample.nom_projet || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>
            Fermer
          </Button>
          <Button 
            variant="contained"
            startIcon={<Visibility />}
            onClick={() => {
              setDialogOpen(false);
              if (scannedSample) {
                navigate(`/samples/${scannedSample.id}`);
              }
            }}
          >
            Voir Détails
          </Button>
        </DialogActions>
      </Dialog>

      <style jsx>{`
        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            opacity: 1;
          }
        }
      `}</style>
    </Box>
  );
};

export default QRScanner; 